import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { FileTypes } from '../../enums/fileTypes';
import { UploadFlows } from '../../enums/uploadFlows';
import { ROOT_DISPLAY_NAME } from '../../views/FileManager/constants';

interface LegendUIContextType {
  open: boolean;
  dropDownText: string;
  selectedOption: string;
  modalVisible: boolean;
  attributeModalVisible: boolean;
  saveLayerModalVisible: boolean;
  selectedTab: number;
  drawFeatureModalVisible: boolean;
  queryToolModalVisible: boolean;
  queryResultModalVisible: boolean;
  proximityToolModalVisible: boolean;
  sketchDrawerVisible: boolean;
  // US-101373 Scenario 9: "Show Hidden Layers" persists across in-app
  // navigation. Storing it on the app-scoped LegendUIProvider survives the
  // ExpandedMapView remount you get when navigating between maps.
  showHiddenLayers: boolean;
  setShowHiddenLayers: (visible: boolean) => void;
  // US-101368 Scenario 1: signal from the legend's 3-dot menu that the
  // Re-Upload Data action should fire the moment Layer Properties' Settings
  // tab mounts (consumer clears it on use).
  reuploadRequested: boolean;
  setReuploadRequested: (requested: boolean) => void;
  // null when an already-saved layer is being edited.
  layerUploadFlow: UploadFlows | null;
  setLayerUploadFlow: (flow: UploadFlows | null) => void;
  // MapView owns the upload chooser modals but is a sibling of the drawer, so
  // it registers the reopen handler here. Returns an unregister fn.
  registerUploadAnotherHandler: (handler: () => void) => () => void;
  requestUploadAnother: () => void;
  setOpen: (open: boolean) => void;
  setDropDownText: (text: string) => void;
  setSelectedOption: (option: string) => void;
  setModalVisible: (visible: boolean) => void;
  setAttributeModalVisible: (visible: boolean) => void;
  setSaveLayerModalVisible: (visible: boolean) => void;
  setSelectedTab: (tab: number) => void;
  setDrawFeatureModalVisible: (visible: boolean) => void;
  setQueryToolModalVisible: (visible: boolean) => void;
  setQueryResultModalVisible: (visible: boolean) => void;
  setProximityToolModalVisible: (visible: boolean) => void;
  setSketchDrawerVisible: (visible: boolean) => void;
}

interface LegendFormContextType {
  folderId: string;
  folderLocation: string;
  uploadFileType: FileTypes;
  uploadedFiles: any;
  selectedSketch: any;
  selectedSketchItem: any;
  sketchDrawerState: string;
  S3UniqueFolderName?: string;
  folderS3Key?: string;
  datasetId?: string;
  setDatasetId: (value?: string) => void;
  setFolderLocation: (value: string) => void;
  setFolderId: (value: string) => void;
  setUploadFileType: (value: FileTypes) => void;
  setUploadedFiles: (files: any) => void;
  setSelectedSketch: (sketch: any) => void;
  setSelectedSketchItem: (sketchItem: any) => void;
  setSketchDrawerState: (state: string) => void;
  setS3UniqueFolderName: (value: string) => void;
  setFolderS3Key: React.Dispatch<React.SetStateAction<string>>;
}

const LegendUIContext = createContext<LegendUIContextType | undefined>(undefined);
const LegendFormContext = createContext<LegendFormContextType | undefined>(undefined);

export const LegendUIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [dropDownText, setDropDownText] = useState('Legend Item 1');
  const [selectedOption, setSelectedOption] = useState('Legend');
  const [modalVisible, setModalVisible] = useState(false);
  const [attributeModalVisible, setAttributeModalVisible] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [saveLayerModalVisible, setSaveLayerModalVisible] = useState(false);
  const [drawFeatureModalVisible, setDrawFeatureModalVisible] = useState(false);
  const [queryToolModalVisible, setQueryToolModalVisible] = useState(false);
  const [queryResultModalVisible, setQueryResultModalVisible] = useState(false);
  const [proximityToolModalVisible, setProximityToolModalVisible] = useState(false);
  const [sketchDrawerVisible, setSketchDrawerVisible] = useState(false);
  const [showHiddenLayers, setShowHiddenLayers] = useState(false);
  const [reuploadRequested, setReuploadRequested] = useState(false);
  const [layerUploadFlow, setLayerUploadFlow] = useState<UploadFlows | null>(null);

  const uploadAnotherHandlerRef = useRef<(() => void) | null>(null);
  const registerUploadAnotherHandler = useCallback((handler: () => void) => {
    uploadAnotherHandlerRef.current = handler;
    return () => {
      if (uploadAnotherHandlerRef.current === handler) uploadAnotherHandlerRef.current = null;
    };
  }, []);
  const requestUploadAnother = useCallback(() => uploadAnotherHandlerRef.current?.(), []);

  return (
    <LegendUIContext.Provider
      value={{
        open,
        setOpen,
        dropDownText,
        setDropDownText,
        selectedOption,
        setSelectedOption,
        modalVisible,
        setModalVisible,
        attributeModalVisible,
        setAttributeModalVisible,
        selectedTab,
        setSelectedTab,
        saveLayerModalVisible,
        setSaveLayerModalVisible,
        drawFeatureModalVisible,
        setDrawFeatureModalVisible,
        queryToolModalVisible,
        setQueryToolModalVisible,
        queryResultModalVisible,
        setQueryResultModalVisible,
        proximityToolModalVisible,
        setProximityToolModalVisible,
        sketchDrawerVisible,
        setSketchDrawerVisible,
        showHiddenLayers,
        setShowHiddenLayers,
        reuploadRequested,
        setReuploadRequested,
        layerUploadFlow,
        setLayerUploadFlow,
        registerUploadAnotherHandler,
        requestUploadAnother,
      }}
    >
      {children}
    </LegendUIContext.Provider>
  );
};

export const LegendFormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedSketch, setSelectedSketch] = useState(null);
  const [selectedSketchItem, setSelectedSketchItem] = useState(null);
  const [sketchDrawerState, setSketchDrawerState] = useState('create');
  const [folderLocation, setFolderLocation] = useState(ROOT_DISPLAY_NAME);
  const [folderId, setFolderId] = useState('');
  const [uploadFileType, setUploadFileType] = useState<FileTypes>(FileTypes.SHAPEFILE);
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const [S3UniqueFolderName, setS3UniqueFolderName] = useState<string>('');
  const [folderS3Key, setFolderS3Key] = useState<string>('');
  const [datasetId, setDatasetId] = useState<string | undefined>();

  return (
    <LegendFormContext.Provider
      value={{
        selectedSketch,
        setSelectedSketch,
        selectedSketchItem,
        setSelectedSketchItem,
        sketchDrawerState,
        setSketchDrawerState,
        folderLocation,
        setFolderLocation,
        folderId,
        setFolderId,
        uploadFileType,
        setUploadFileType,
        uploadedFiles,
        setUploadedFiles,
        setS3UniqueFolderName,
        S3UniqueFolderName,
        setFolderS3Key,
        folderS3Key,
        datasetId,
        setDatasetId,
      }}
    >
      {children}
    </LegendFormContext.Provider>
  );
};

export const LegendDrawerProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <LegendFormProvider>
    <LegendUIProvider>{children}</LegendUIProvider>
  </LegendFormProvider>
);

export const useLegendUI = () => {
  const context = useContext(LegendUIContext);
  if (context === undefined) {
    throw new Error('useLegendUI must be used within a LegendUIProvider');
  }
  return context;
};

export const useLegendForm = () => {
  const context = useContext(LegendFormContext);
  if (context === undefined) {
    throw new Error('useLegendForm must be used within a LegendFormProvider');
  }
  return context;
};
